import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { TypedFormControl } from '@principle-theorem/ng-shared';
import {
  getServiceCodeNumberString,
  ServiceCodeGroup,
  ServiceProvider,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import { SERVICE_PROVIDER_FEATURE_MAP } from '@principle-theorem/principle-core/features';
import {
  type IServiceCodeGroup,
  type IServiceSmartGroup,
  type ITreatmentConfiguration,
  ServiceCodeGroupType,
  SMART_GROUPS,
  IServiceProvider,
  ServiceCode,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  multiSwitchMap,
  patchDoc,
  Region,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, isString, uniq } from 'lodash';
import { combineLatest, type Observable, of, ReplaySubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface IServiceCodeSection {
  label: string;
  groups: IServiceCodeGroup[];
  serviceCodeType?: ServiceCodeType;
}

const unscopedSectionLabel = 'Applies to all fee schedules';

@Component({
  selector: 'pr-item-code-configuration',
  templateUrl: './item-code-configuration.component.html',
  styleUrls: ['./item-code-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ItemCodeConfigurationComponent {
  treatmentConfig$ = new ReplaySubject<WithRef<ITreatmentConfiguration>>(1);
  smartGroupSelector: TypedFormControl<string | IServiceSmartGroup> =
    new TypedFormControl();
  filteredSmartGroups$: Observable<IServiceSmartGroup[]>;
  serviceProviders$: Observable<IServiceProvider[]>;
  sections$: Observable<IServiceCodeSection[]>;
  scopingEnabled$: Observable<boolean>;
  region = Region;

  @Input()
  set treatmentConfig(treatmentConfig: WithRef<ITreatmentConfiguration>) {
    if (treatmentConfig) {
      this.treatmentConfig$.next(treatmentConfig);
    }
  }

  constructor(
    private _snackBar: MatSnackBar,
    org: OrganisationService,
    featureFlags: FeatureFlagsService
  ) {
    const searchValue$ = this.smartGroupSelector.valueChanges.pipe(
      startWith('')
    );

    const enabledProviderLabels$ = of(
      Object.values(SERVICE_PROVIDER_FEATURE_MAP)
    ).pipe(
      multiSwitchMap((label) =>
        featureFlags
          .isFeatureEnabledByLabel$(label)
          .pipe(map((enabled) => (enabled ? label : undefined)))
      ),
      map((labels) => compact(uniq(labels)))
    );

    this.serviceProviders$ = combineLatest([
      org.organisation$.pipe(filterUndefined()),
      enabledProviderLabels$,
    ]).pipe(
      map(([organisation, enabledLabels]) =>
        ServiceProvider.providersByRegion(organisation.region).filter(
          (provider) => {
            const featureLabel = SERVICE_PROVIDER_FEATURE_MAP[provider.type];
            return !featureLabel || enabledLabels.includes(featureLabel);
          }
        )
      )
    );

    this.scopingEnabled$ = org.region$.pipe(
      map((region) => region === Region.NewZealand)
    );

    this.sections$ = combineLatest([
      this.treatmentConfig$,
      this.serviceProviders$,
      this.scopingEnabled$,
    ]).pipe(
      map(([treatmentConfig, serviceProviders, scopingEnabled]) =>
        scopingEnabled
          ? this._buildSections(
              treatmentConfig.serviceCodeGroups,
              serviceProviders
            )
          : []
      )
    );

    this.filteredSmartGroups$ = combineLatest([
      of(SMART_GROUPS),
      searchValue$,
    ]).pipe(
      map(([smartGroups, searchValue]) => {
        const search: string = isString(searchValue) ? searchValue : '';
        return this._filterSmartGroups(smartGroups, search);
      })
    );
  }

  async addGroup(serviceCodeType?: ServiceCodeType): Promise<void> {
    const treatmentConfig = await snapshot(this.treatmentConfig$);
    await this.save({
      serviceCodeGroups: [
        ...treatmentConfig.serviceCodeGroups,
        this._getNewGroup(ServiceCodeGroupType.Optional, serviceCodeType),
      ],
    });
  }

  async addSmartGroup(smartGroup: IServiceSmartGroup): Promise<void> {
    const treatmentConfig = await snapshot(this.treatmentConfig$);
    this.clearInput();
    await this.save({
      serviceCodeSmartGroups: [
        ...treatmentConfig.serviceCodeSmartGroups,
        smartGroup,
      ],
    });
  }

  clearInput(): void {
    this.smartGroupSelector.reset();
  }

  async deleteSmartEntry(entry: IServiceSmartGroup): Promise<void> {
    const treatmentConfig = await snapshot(this.treatmentConfig$);
    await this.save({
      serviceCodeSmartGroups: TreatmentConfiguration.removeSmartGroup(
        treatmentConfig.serviceCodeSmartGroups,
        entry
      ),
    });
  }

  async updateGroups(serviceCodeGroups: IServiceCodeGroup[]): Promise<void> {
    await this.save({
      serviceCodeGroups,
    });
  }

  async updateScopedGroups(
    serviceCodeType: ServiceCodeType | undefined,
    serviceCodeGroups: IServiceCodeGroup[]
  ): Promise<void> {
    const treatmentConfig = await snapshot(this.treatmentConfig$);
    await this.save({
      serviceCodeGroups: ServiceCodeGroup.replaceScope(
        treatmentConfig.serviceCodeGroups,
        serviceCodeType,
        serviceCodeGroups
      ),
    });
  }

  async save(
    itemCodes: Partial<
      Pick<
        ITreatmentConfiguration,
        'serviceCodeGroups' | 'serviceCodeSmartGroups'
      >
    >
  ): Promise<void> {
    const treatmentConfig = await snapshot(this.treatmentConfig$);
    await patchDoc(treatmentConfig.ref, itemCodes);
    this._snackBar.open('Treatment configuration saved');
  }

  summariseCodes(codes: ServiceCode[]): string {
    return codes.map((code) => getServiceCodeNumberString(code)).join(' / ');
  }

  private _filterSmartGroups(
    smartGroup: IServiceSmartGroup[],
    search: string
  ): IServiceSmartGroup[] {
    const filterValue = search.toLowerCase();

    return smartGroup.filter((item: IServiceSmartGroup) =>
      `${item.uid} - ${item.title}`.toLowerCase().includes(filterValue)
    );
  }

  private _buildSections(
    serviceCodeGroups: IServiceCodeGroup[],
    serviceProviders: IServiceProvider[]
  ): IServiceCodeSection[] {
    const scopedSections = serviceProviders.map((provider) => ({
      label: provider.label,
      serviceCodeType: provider.type,
      groups: serviceCodeGroups.filter(
        (group) => group.serviceCodeType === provider.type
      ),
    }));
    const unscopedGroups = serviceCodeGroups.filter(
      (group) => !group.serviceCodeType
    );

    return unscopedGroups.length
      ? [
          {
            label: unscopedSectionLabel,
            groups: unscopedGroups,
          },
          ...scopedSections,
        ]
      : scopedSections;
  }

  private _getNewGroup(
    type: ServiceCodeGroupType = ServiceCodeGroupType.Optional,
    serviceCodeType?: ServiceCodeType
  ): IServiceCodeGroup {
    return ServiceCodeGroup.init({ type, serviceCodeType, serviceCodes: [] });
  }
}
